import { confirm, IconWarning, toast, useLocalization, createMutation, createTriggerResource } from '@magicdoorinc/ui';
import { createSignal } from 'solid-js';
import { useChartOfAccounts } from '~/contexts/chart-of-accounts/ChartOfAccountsContext';
import { createMagicDoorContext } from '~/contexts/utils';
import { mode, PaymentMode } from '~/hooks/usePaymentMode';
import { BankAccountRepository } from '~/repositories/bankAccountRepository';
import { BankAccountConnectType } from '~/swagger/portal/Api';
import { useBankAccountsList } from './BankAccountsListContext';
import type { BankAccountSweepsFilter } from '~/repositories/bankAccountRepository';
import type {
  BankAccountConnectionsDto,
  ConnectBankAccountDto,
  CreateBankAccountDto,
  HydratedBankAccountDto,
  UpdateBankAccountDto,
} from '~/swagger/portal/Api';

export const [BankAccountProvider, useBankAccount] = createMagicDoorContext('BankAccount', () => {
  const bankAccountListContext = useBankAccountsList();
  const chartOfAccountsContext = useChartOfAccounts();
  const { t } = useLocalization();
  const repo = new BankAccountRepository();

  const [bankAccount, setBankAccount] = createSignal<HydratedBankAccountDto | undefined>(undefined);
  const [bankAccountId, setBankAccountId] = createSignal<string | undefined>(undefined);
  const [isLoading, setIsLoading] = createSignal<boolean>(false);
  const [error, setError] = createSignal<Error | undefined>(undefined);
  const [bankAccountPlaid, setBankAccountPlaid] = createSignal<BankAccountConnectionsDto['plaid']>();
  const [bankAccountStripe, setBankAccountStripe] = createSignal<BankAccountConnectionsDto['stripe']>();

  const [bankAccountSweeps, getBankAccountSweeps] = createTriggerResource(async (filters: BankAccountSweepsFilter) => {
    const pagination = await repo.getBankAccountSweeps(filters);
    return {
      items: pagination.items,
      page: pagination.currentPage,
      pageSize: pagination.pageSize,
      totalPages: pagination.totalPages,
      totalCount: pagination.totalCount,
    };
  });

  const getBankAccountSweepDetails = async (bankAccountId: string, sweepId: string) => {
    return repo.getBankAccountSweepDetails(bankAccountId, sweepId);
  };

  const [bankAccountPayouts, getBankAccountPayouts] = createTriggerResource(async (bankAccountId: string) => {
    return await repo.getBankAccountPayouts(bankAccountId);
  });

  const getBankAccountPayoutDetails = async (bankAccountId: string, payoutId: string) => {
    return repo.getBankAccountPayout(bankAccountId, payoutId);
  };

  const fetchBankAccountById = async (bankAccountId: string | undefined) => {
    if (bankAccountId === undefined) return;
    try {
      setIsLoading(true);
      const data = await repo.getBankAccount(bankAccountId);
      setBankAccount(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const addBankAccount = async (bankAccountData: CreateBankAccountDto) => {
    setIsLoading(true);
    try {
      const data = {
        ...bankAccountData,
        scheduledSweepDays: mode() === PaymentMode.Pay ? [] : (bankAccountData.scheduledSweepDays ?? []),
      };
      const newBankAccount = await repo.createBankAccount(data);
      if (newBankAccount) {
        bankAccountListContext.refetchBankAccounts();
        chartOfAccountsContext.refetch();
        setBankAccount(newBankAccount);
      }
      return newBankAccount;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateBankAccount = async (
    bankAccountId: string,
    bankAccountData: UpdateBankAccountDto
  ): Promise<HydratedBankAccountDto | undefined> => {
    try {
      const data = {
        ...bankAccountData,
        scheduledSweepDays: mode() === PaymentMode.Pay ? [] : (bankAccountData.scheduledSweepDays ?? []),
      };
      const updatedBankAccount = await repo.updateBankAccount(bankAccountId, data);
      if (updatedBankAccount !== undefined) {
        setBankAccount(updatedBankAccount);
      }

      bankAccountListContext.refetchBankAccounts();
      bankAccountListContext.setUpdateConnections({ bankAccountId });
      return updatedBankAccount;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  };

  const deleteBankAccount = async (bankAccountId: string) => {
    setIsLoading(true);
    try {
      await repo.deleteBankAccount(bankAccountId);
      bankAccountListContext.refetchBankAccounts();
      setBankAccount(undefined);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getBankAccountConnections = async (bankAccountId: string) => {
    try {
      const result = await repo.getBankAccountConnections(bankAccountId);
      setBankAccountPlaid(result.plaid);
      setBankAccountStripe(result.stripe);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  };

  const connectToPlaid = createMutation(async (bankAccountId: string, connectParams: ConnectBankAccountDto) => {
    await repo.bankAccountConnections({ type: BankAccountConnectType.Plaid, bankAccountId, ...connectParams });
  });

  const plaidSweeps = async (bankAccountId: string) => {
    setIsLoading(true);
    try {
      await repo.plaidSweeps(bankAccountId);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getSweepsLimits = async () => {
    try {
      const result = await repo.getSweepsLimits();
      return result;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  };

  const handleSweep = async (bankAccountId: string, successCallback?: () => void) => {
    confirm({
      icon: () => <IconWarning class="my-1 size-5 text-warning" />,
      title: t('Sweep'),
      titleClass: 'text-warning',
      content: t('Are you sure you want to perform this operation?'),
      doneText: t('Confirm'),
      class: 'w-[380px]',
      async onResolve(confirmed) {
        if (!confirmed) return;
        try {
          await plaidSweeps(bankAccountId);
        } catch {
          return;
        }
        toast.success(t('Operation successful'));
        successCallback?.();
      },
    });
  };

  return {
    addBankAccount,
    updateBankAccount,
    deleteBankAccount,
    fetchBankAccountById,
    setBankAccountId,
    bankAccount,
    isLoading,
    error,
    getBankAccountConnections,
    bankAccountPlaid,
    bankAccountStripe,
    connectToPlaid,
    bankAccountId,
    setBankAccount,
    plaidSweeps,
    getSweepsLimits,
    bankAccountSweeps,
    getBankAccountSweeps,
    getBankAccountSweepDetails,
    bankAccountPayouts,
    getBankAccountPayouts,
    getBankAccountPayoutDetails,
    handleSweep,
  };
});
